/* eslint-disable sonarjs/cognitive-complexity */
import cn from 'classnames';
import React, { FC, useMemo, useState } from 'react';
import { isMobileOnly, useMobileOrientation } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useGDLibContext } from 'src/contexts/GDLibContext';
import { addGameSearchParams } from 'src/helpers/gameQueriesManager';
import { checkDeviceCompatibility, envStrToBoolean, setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useGameBoxReady } from 'src/hooks/use-gamebox-ready';
import { useGameAction } from 'src/hooks/useGameAction';
import { useNearViewport } from 'src/hooks/useNearViewport';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { setSelectedGame } from 'src/store/games/actions';
import { RootState } from 'src/types/store-types';
import { GAME_CARD_SIZES, TEMPLATE_RADIUS } from 'src/utils/constants';
import GameCardSceleton from '../GameCardSceleton/GameCardSceleton';
import PopUpContainer from '../modal/PopUp';
import { SvgIcon } from '../SvgIcon';
import { Typography } from '../Typography';
import './styles.scss';

type GameCard = {
  data: {
    thumbnail: string;
    name: string;
    providerName: string;
    id: number;
    cols?: number;
    rows?: number;
    productId?: any;
    gameKey?: any;
  };
  showDetails?: boolean;
  loader?: string;
  useRatio?: boolean;
  effects: {
    hoverType?: string;
    src?: string;
    text?: string;
  };
  template?: string;
  size: 'lg' | 'md_l' | 'md_m' | 'md_s' | 'md_xs' | 'sm_l' | 'sm';
  useGif?: boolean;
};
const GameCard: FC<GameCard> = ({
  data,
  showDetails,
  effects,
  size,
  useRatio = false,
  template = 'template_noFullScreen',
  useGif = false,
}) => {
  const [searchParams, setQueryParams] = useSearchParams();
  const { isPortrait } = useMobileOrientation();
  const { t }: Translation = useTranslation();
  const { thumbnail, name, providerName } = data;
  const { hoverType, src, text } = effects;
  const deviceCompatibilityStatus = checkDeviceCompatibility().compatible;

  const _openAuthModal = useConnectedAction(openAuthModal);
  const _selectedGame = useConnectedAction(setSelectedGame);
  const _openDialog = useConnectedAction(openDialog);

  const { user, gamePreviewProducts } = useSelector((state: RootState) => state.user);
  const { generalConfigs } = useSelector((state: RootState) => state.configs);
  const { cookiesAccepted } = useSelector((state: RootState) => state.app);
  const [hovered, setHovered] = useState<boolean>(false);

  const [imageRef, isNear] = useNearViewport<HTMLDivElement>();

  // Get GDLib context
  const { initializeGDLib } = useGDLibContext();

  const { shouldShowLimits, shouldShowKyc } = useGameAction();

  // Use GameBox readiness hook
  const gameBoxReady = useGameBoxReady({
    onSuccess: () => {
      _selectedGame(data);
      addGameSearchParams(searchParams, setQueryParams, data);
    },
    getBaseDescription: () => t('gameSlowText'),
    initializeGDLib,
  });

  const templateKey =
    generalConfigs?.template?.split?.(' ')?.[0] ?? generalConfigs?.template ?? 'template_noFullScreen';

  const cardSizes = useMemo(() => {
    if (isMobileOnly) {
      if (showDetails) {
        return {
          height: 'auto',
          width: '29vw',
        };
      }

      return {
        height: `${GAME_CARD_SIZES[size]}px`,
        width: template === 'template_fullScreen' ? '29vw' : `${GAME_CARD_SIZES[size]}px`,
      };
    }

    return {
      height: showDetails ? '240px' : `${GAME_CARD_SIZES[size] - (template === 'template_fullScreen' ? 20 : 0)}px`,
      width: useRatio ? 'max-content' : `${GAME_CARD_SIZES[size] + (showDetails ? 20 : 0)}px`,
    };
  }, [size, window.innerWidth, showDetails, useRatio, template, isPortrait]);

  const imgSizes = useMemo(() => {
    if (isMobileOnly) {
      if (showDetails) {
        return {
          height: '105px',
          width: '105px',
        };
      }

      return {
        height: `${(window.innerWidth * 28) / 100}px`,
        width: `${(window.innerWidth * 28) / 100}px`,
      };
    }

    return {
      height: showDetails ? '190px' : `${GAME_CARD_SIZES[size] - (template === 'template_fullScreen' ? 20 : 0)}px`,
      width: useRatio ? 'max-content' : `${GAME_CARD_SIZES[size]}px`,
    };
  }, [size, window.innerWidth, showDetails, isPortrait]);

  const handleHoverStart = (): void => {
    setHovered(true);
  };

  const handleHoverEnd = (): void => {
    setHovered(false);
  };

  const onUnhoveredGameClick = (): void => {
    !hoverType && onGameClick();
  };

  const onGameClick = (): void => {
    if (
      envStrToBoolean(process.env.REACT_APP_USE_COOKIES_GAMECARD) &&
      envStrToBoolean(process.env.REACT_APP_USE_COOKIES) &&
      !cookiesAccepted
    ) {
      _openDialog({
        dialogType: EnumDialogsKeys.COOKIES_POPUP,
        dialogProps: { data: {} },
      });
    } else if (envStrToBoolean(process.env.REACT_APP_DEVICE_COMPATIBILITY_ENABLE) && !deviceCompatibilityStatus) {
      _openDialog({
        dialogType: EnumDialogsKeys.DEVICE_COMPATIBILITY_POPUP,
        dialogProps: { data: {} },
      });
    } else if (shouldShowKyc) {
      _openDialog({
        dialogType: EnumDialogsKeys.KYC_POPUP,
        dialogProps: { data: {} },
      });
    } else if (shouldShowLimits) {
      _openDialog({
        dialogType: EnumDialogsKeys.SET_LIMITS_AFTER_LOGIN_POPUP,
        dialogProps: { data: {} },
      });
    } else {
      if (user) {
        setBodyOverflow('unset');
        if (generalConfigs?.gameLaunchPreview && gamePreviewProducts.includes(data.productId)) {
          _openDialog({
            dialogType: EnumDialogsKeys.GAME_LAUNCH_PREVIEW,
            dialogProps: { data: data },
          });
        } else {
          // Check GameBox readiness using hook
          if (!gameBoxReady.checkReadiness()) {
            console.log('[GameCard] GameBox not ready, showing popup...');
            gameBoxReady.showPopup();
            return;
          }

          console.log('[GameCard] Success! The game has been opened.');
          _selectedGame(data);
          addGameSearchParams(searchParams, setQueryParams, data);
        }
      } else {
        setBodyOverflow('unset');
        _openAuthModal('login');
      }
    }
  };

  const handleClosePopup = (): void => {
    gameBoxReady.handleClose();
    isMobileOnly && setBodyOverflow('set');
  };

  return (
    <>
      {gameBoxReady.isPopUpOpen && (
        <PopUpContainer
          isOpen={gameBoxReady.isPopUpOpen}
          hasOverlay
          handleClose={handleClosePopup}
          handleOK={gameBoxReady.handleRetry}
          description={gameBoxReady.description}
          confirmText={t('retry')}
        />
      )}
      <div className={cn('game', { ['showDetails']: showDetails })} style={cardSizes} onClick={onUnhoveredGameClick}>
        <div
          ref={imageRef}
          className="image_wrapper"
          style={template === 'template_2' ? cardSizes : {}}
          onMouseEnter={handleHoverStart}
          onMouseLeave={handleHoverEnd}
          onFocus={handleHoverStart}
          onBlur={handleHoverEnd}
          tabIndex={0}
        >
          {thumbnail && isNear && (
            <img
              src={thumbnail}
              loading="lazy"
              style={{
                height: imgSizes.height,
                width: imgSizes.width,
                minWidth: imgSizes.width,
                borderRadius: TEMPLATE_RADIUS[templateKey],
              }}
              className="game__image"
              alt={name}
            />
          )}
          <GameCardSceleton
            useGif={useGif}
            height={imgSizes.height}
            width={imgSizes.width}
            borderRadius={TEMPLATE_RADIUS[templateKey]}
          />
          {hovered && hoverType && (
            <div
              className="game_overlay"
              style={{
                borderRadius: TEMPLATE_RADIUS[templateKey],
              }}
            >
              {hoverType === 'fullWidthImage' ? (
                <div className="img_play" onClick={onGameClick}></div>
              ) : (
                <div className="btn_play">
                  {src && <SvgIcon src={src} className="btn_play_icon" />}
                  {text}
                </div>
              )}
            </div>
          )}
        </div>
        {showDetails && (
          <div className="details">
            <Typography variant="h6" className="games__title">
              {name}
            </Typography>
            <Typography variant="h6" className="games__provider_name">
              {providerName}
            </Typography>
          </div>
        )}
      </div>
    </>
  );
};
export default GameCard;
